import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { MAX_DASHBOARD_NAME_LENGTH } from '~/common/constants/max_lengths';
import { trackSegment } from '~/components/SegmentAnalytics';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { Box } from '~/eds/src/components/Box/Box';
import { useModal } from '~/eds/src/components/hooks/useModal';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { SearchQuery } from '~/features/advanced-search/types';
import { SaveActionButton } from '~/features/search/SaveActionButton/SaveActionButton';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { api } from '~/redux/api/createApi';
import {
  Dashboard as DashboardEntity,
  generateDashboardToSave,
} from '~/redux/api/methods/dashboardV2';
import { coerceRtkqError } from '~/redux/api/utils';
import { useRouting } from '~/routing/useRouting';
import { Nullable } from '~/types/Nullable';
import { ERROR, SUCCESS } from '~/types/toast.types';

type Props = {
  dashboardData: DashboardEntity;
  query: SearchQuery;
  isDashboardDirty: boolean;
  hasSavePermission: boolean;
  onSaveSucess: () => void;
};

const SaveDashboard = ({
  dashboardData,
  query,
  hasSavePermission,
  isDashboardDirty,
  onSaveSucess,
}: Props) => {
  const intl = useIntl();
  const { navigate } = useRouting();
  const user = useCurrentUser();
  const enableModalConsolidation = useFlag(FlagType.ModalConsolidation);
  const { toast } = useToast();

  const [visible, setVisible] = useState(false);

  const [saveAsDashboard, saveDashboardResults] =
    api.endpoints.saveAsDashboard.useMutation();
  const {
    data,
    error: savingError,
    isError: isSavingError,
    isLoading: isSaving,
    isSuccess: isSavingSuccess,
  } = saveDashboardResults;

  const [saveDashboardChanges] =
    api.endpoints.saveDashboardChanges.useMutation();

  const placeholderName = `${dashboardData.name} (1)`;
  const [name, setName] = useState<Nullable<string>>(placeholderName);

  const handleSubmit = () => {
    const dashboard = generateDashboardToSave(
      dashboardData,
      dashboardData.id,
      query,
    );
    dashboard.attributes = { ...dashboard.attributes, name: name as string };
    const { id, ...dashboardToSaveAs } = dashboard;
    trackSegment('saveAsDashboard', {
      dashboard_id: id,
      dashboard_name: name,
      user_id: user.id,
      client_id: user.client,
    });
    saveAsDashboard(dashboardToSaveAs);
  };

  const handleHide = () => {
    setName(placeholderName);
  };

  useEffect(() => {
    if (isSavingError) {
      const resp = coerceRtkqError(savingError)?.response || {};
      const msg = resp.data?.errors?.[0];
      showToast(
        ERROR,
        msg?.title ||
          intl.formatMessage({
            id: 'SaveDashboard.errorSaving',
            defaultMessage: 'An error occurred while saving the dashboard.',
            description: 'Error message when dashboard save fails',
          }),
      );
    }
  }, [isSavingError]);

  useEffect(() => {
    if (isSavingSuccess) {
      showToast(
        SUCCESS,
        intl.formatMessage({
          id: 'SaveDashboard.saveSuccess',
          defaultMessage: 'Dashboard has been saved successfully.',
          description: 'Success message when dashboard is saved',
        }),
      );

      navigate(`/dashboards/${data?.data.id}`);
    }
  }, [isSavingSuccess]);

  const getTooltip = () => {
    if (!name?.trim()) {
      return intl.formatMessage({
        id: 'SaveDashboard.enterNameTooltip',
        defaultMessage: 'Please enter a name.',
        description: 'Tooltip when dashboard name is empty',
      });
    } else if (name?.length > MAX_DASHBOARD_NAME_LENGTH) {
      return intl.formatMessage(
        {
          id: 'SaveDashboard.nameLengthTooltip',
          defaultMessage:
            'A dashboard name can not exceed {maxLength} characters.',
          description:
            'Tooltip when dashboard name exceeds length limit. Example: maxLength = "128"',
        },
        { maxLength: MAX_DASHBOARD_NAME_LENGTH },
      );
    } else {
      return '';
    }
  };

  const handleSave = async () => {
    const dashboard = generateDashboardToSave(
      dashboardData,
      dashboardData.id,
      query,
    );

    trackSegment('selectEditDashboard', {
      id: dashboard.id,
      name: dashboard.attributes.name,
      user_id: user.id,
      client_id: user.client,
    });
    try {
      await saveDashboardChanges({
        uuid: dashboard.id ?? '',
        data: dashboard,
      }).unwrap();
      onSaveSucess();
      toast({
        message: intl.formatMessage({
          id: 'SaveDashboard.saveSuccessToast',
          defaultMessage: 'Dashboard has been saved successfully.',
          description: 'Toast message when dashboard is saved successfully',
        }),
        status: 'success',
      });
    } catch {
      toast({
        message: intl.formatMessage({
          id: 'SaveDashboard.saveErrorToast',
          defaultMessage: 'An error occurred while saving the dashboard.',
          description: 'Toast message when dashboard save fails',
        }),
        status: 'danger',
      });
    }
  };
  const children = (
    <Layout direction="column" h="100%" spacing={6}>
      <Box flex="auto" overflowY="auto">
        <TextInput
          autoFocus={true}
          disabled={isSaving}
          name="save as dashboard"
          placeholder={intl.formatMessage({
            id: 'SaveDashboard.namePlaceholder',
            defaultMessage: 'Enter a dashboard name',
            description: 'Placeholder text for dashboard name input',
          })}
          value={name}
          onChange={setName}
        />
      </Box>
    </Layout>
  );

  const primaryAction = {
    disabled:
      !name?.trim() || name?.length > MAX_DASHBOARD_NAME_LENGTH || isSaving,
    isLoading: isSaving,
    text: intl.formatMessage({
      id: 'SaveDashboard.saveButton',
      defaultMessage: 'Save',
      description: 'Save button text',
    }),
    tooltip: getTooltip(),
    onClick: handleSubmit,
  };
  const title = intl.formatMessage({
    id: 'SaveDashboard.modalTitle',
    defaultMessage: 'Save as New Dashboard',
    description: 'Modal title for saving dashboard',
  });

  const [modal, showModal] = useModal({
    children,
    primaryAction,
    title,
    width: 'm',
    onHide: handleHide,
  });

  const panel = (
    <Panel
      title={title}
      hidden={{
        onHide: () => {
          setVisible(false);
          handleHide();
        },
      }}
      footer={{
        actions: [
          {
            mode: 'chip',
            text: intl.formatMessage({
              id: 'SaveDashboard.cancelButton',
              defaultMessage: 'Cancel',
              description: 'Cancel button text',
            }),
            onClick: () => {
              setVisible(false);
              handleHide();
            },
          },
          { ...primaryAction, level: 'primary', mode: 'chip' },
        ],
      }}
      children={children}
      width="560px"
      mode="floating"
      enableBackdrop
    />
  );

  const isOverwriteDisabled =
    dashboardData.is_default || !isDashboardDirty || !hasSavePermission;

  return (
    <>
      <SaveActionButton
        entity="Dashboard"
        saveAsNewAction={{
          onClick: enableModalConsolidation
            ? () => setVisible(!visible)
            : showModal,
        }}
        overwriteAction={
          !dashboardData.is_default
            ? {
                onClick: handleSave,
                tooltip: hasSavePermission
                  ? ''
                  : intl.formatMessage({
                      id: 'SaveDashboard.noPrivilegesMessage',
                      defaultMessage:
                        'You do not have privileges to save this dashboard.',
                      description: 'Tooltip when user lacks save privileges',
                    }),
                disabled: isOverwriteDisabled,
              }
            : undefined
        }
      />
      {!enableModalConsolidation && modal}
      {enableModalConsolidation && visible && panel}
    </>
  );
};

export default SaveDashboard;
